// src/components/TermsReacceptModal.jsx
// Modal que aparece cuando el usuario tiene una versión de T&C desactualizada.
// Se monta globalmente en App.js — no requiere cambios en cada página.

import { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';

const font = 'Montserrat, sans-serif';

export default function TermsReacceptModal() {
  const { needsTermsReaccept, acceptTerms, logout } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  if (!needsTermsReaccept) return null;

  const handleAccept = async () => {
    setError('');
    setLoading(true);
    try {
      await acceptTerms();
      // needsTermsReaccept pasa a false en el contexto → modal se cierra solo
    } catch {
      setError('No se pudo registrar la aceptación. Intentá de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
  };

  return (
    // Overlay
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(15, 34, 51, 0.75)',
      backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '20px',
    }}>
      {/* Card */}
      <div style={{
        background: '#ffffff',
        borderRadius: 16,
        boxShadow: '0 24px 64px rgba(0,0,0,0.18)',
        maxWidth: 480,
        width: '100%',
        overflow: 'hidden',
      }}>

        {/* Header */}
        <div style={{
          background: '#163244',
          padding: '20px 24px 18px',
          borderBottom: '3px solid #E7A432',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {/* Ícono */}
            <div style={{
              width: 36, height: 36, borderRadius: 8,
              background: 'rgba(231,164,50,0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#E7A432" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
                <polyline points="10 9 9 9 8 9"/>
              </svg>
            </div>
            <div>
              <div style={{
                fontFamily: font, fontSize: 15, fontWeight: 800,
                color: '#ffffff', letterSpacing: '-0.01em',
              }}>
                Actualizamos nuestros Términos
              </div>
              <div style={{
                fontFamily: font, fontSize: 11, color: 'rgba(255,255,255,0.5)',
                marginTop: 2,
              }}>
                Necesitamos que los revises antes de continuar
              </div>
            </div>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '22px 24px' }}>
          <p style={{
            fontFamily: font, fontSize: 13.5, lineHeight: 1.75,
            color: '#2c3e50', margin: '0 0 16px 0',
          }}>
            Actualizamos los Términos y Condiciones de FIXO. Para seguir usando la plataforma,
            necesitamos que los leas y los aceptes.
          </p>

          {/* Links a documentos */}
          <div style={{
            background: '#EAF0F4',
            border: '1px solid #b8cdd9',
            borderLeft: '4px solid #163244',
            borderRadius: 8, padding: '12px 16px',
            marginBottom: 20,
          }}>
            <div style={{
              fontFamily: font, fontSize: 11, fontWeight: 700,
              color: '#163244', textTransform: 'uppercase',
              letterSpacing: '0.05em', marginBottom: 8,
            }}>
              Documentos actualizados
            </div>
            {[
              { label: 'Términos y Condiciones',  to: '/terminos' },
              { label: 'Política de Privacidad',  to: '/privacidad' },
              { label: 'Política de Cookies',     to: '/cookies' },
              { label: 'Guías de la Comunidad',   to: '/comunidad' },
            ].map(({ label, to }) => (
              <div key={to} style={{ marginBottom: 4 }}>
                <Link
                  to={to}
                  target="_blank"
                  style={{
                    fontFamily: font, fontSize: 13, fontWeight: 600,
                    color: '#163244', textDecoration: 'underline',
                  }}
                >
                  → {label}
                </Link>
              </div>
            ))}
          </div>

          {/* Error */}
          {error && (
            <div style={{
              background: '#fef2f2', border: '1px solid #fca5a5',
              borderRadius: 8, padding: '10px 14px',
              fontFamily: font, fontSize: 12.5, color: '#dc2626',
              marginBottom: 16,
            }}>
              {error}
            </div>
          )}

          {/* Botones */}
          <div style={{ display: 'flex', gap: 10, flexDirection: 'column' }}>
            <button
              onClick={handleAccept}
              disabled={loading}
              style={{
                width: '100%', padding: '12px 0',
                background: loading ? 'rgba(22,50,68,0.5)' : '#163244',
                color: '#ffffff', border: 'none',
                borderRadius: 50, cursor: loading ? 'not-allowed' : 'pointer',
                fontFamily: font, fontSize: 13, fontWeight: 700,
                transition: 'background 0.15s',
              }}
            >
              {loading ? 'Guardando…' : 'He leído y acepto los Términos actualizados'}
            </button>

            <button
              onClick={handleLogout}
              disabled={loading}
              style={{
                width: '100%', padding: '10px 0',
                background: 'transparent', color: '#7a93a4',
                border: '1px solid rgba(0,0,0,0.1)',
                borderRadius: 50, cursor: 'pointer',
                fontFamily: font, fontSize: 12, fontWeight: 500,
                transition: 'color 0.15s',
              }}
              onMouseEnter={e => e.target.style.color = '#163244'}
              onMouseLeave={e => e.target.style.color = '#7a93a4'}
            >
              No acepto — cerrar sesión
            </button>
          </div>

          <p style={{
            fontFamily: font, fontSize: 11, color: '#7a93a4',
            margin: '14px 0 0 0', textAlign: 'center', lineHeight: 1.6,
          }}>
            No podés continuar usando FIXO sin aceptar los Términos vigentes.
            Podés cerrar sesión y volver cuando quieras.
          </p>
        </div>
      </div>
    </div>
  );
}
